import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { produce } from "immer";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import AutosizeTextArea from "../../components/input/AutosizeTextArea";
import SimpleListbox from "../../components/input/SimpleListbox";
import { PEER_REVIEW_MAX_LENGTH } from "./PeerReview";
import {
	PeerReviewViewModel,
	PeerReviewQuestionViewModel,
	usePeerReviewUpdateMutation,
	DraftPeerReviewQuestionAnswer,
	PeerReviewUpdateModel,
} from "./peerReviewsApiSlice";

export interface PeerReviewInteractionProps {
	className?: string;
	draftId: number;
	review: PeerReviewViewModel;
	readOnly: boolean;
	showPrint?: boolean;
}

export function PeerReviewInteraction(props: PeerReviewInteractionProps) {
	const navigate = useNavigate();
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const { role } = useAppSelector((state) => state.user);

	const [questions, setQuestions] = useState(props.review.questions);
	const [questionIndex, setQuestionIndex] = useState<number | undefined>();
	const [starterChanges, setStarterChanges] = useState(0);
	const [isReadOnly, setIsReadOnly] = useState(props.readOnly);

	const canUpdateReview =
		props.review.canEditPage && (role == UserRole.Proctor || !props.review.isReviewed);

	const [updatePeerReview] = usePeerReviewUpdateMutation();

	useEffect(() => {
		// focus on textarea when starter changes
		inputRef.current?.focus();
	}, [starterChanges]);

	const canSubmit = () => {
		if (!questions) return false;
		const unanswered = questions.filter((q) => {
			return (
				!q.comment ||
				q.comment.trim() === "" ||
				(!q.isGeneral && q.answer === DraftPeerReviewQuestionAnswer.NoAnswer)
			);
		});
		return unanswered.length === 0;
	};

	const handlePrint = (event: React.FormEvent<HTMLButtonElement>) => {
		window.print();
	};

	const handleAnswerChange = (index: number, value: string) => {
		if (!questions) return;

		var updatedQuestions = produce(questions, (draftState) => {
			draftState[index].answer = value as DraftPeerReviewQuestionAnswer;
		});

		setQuestions(updatedQuestions);
	};

	const handleCommentChange = (index: number, value: string) => {
		if (!questions) return;

		if (value.length > PEER_REVIEW_MAX_LENGTH) {
			alert(`Comment is limited to ${PEER_REVIEW_MAX_LENGTH} characters.`);
			value = value.substring(0, PEER_REVIEW_MAX_LENGTH);
		}

		var updatedQuestions = produce(questions, (draftState) => {
			draftState[index].comment = value;
		});

		setQuestions(updatedQuestions);
	};

	const handleStarterChange = (index: number, value: string) => {
		if (!questions) return;

		var updatedQuestions = produce(questions, (draftState) => {
			draftState[index].comment = value;
		});

		setQuestionIndex(index);
		setStarterChanges(starterChanges + 1);
		setQuestions(updatedQuestions);
	};

	const handleSaveOrSubmit = (event: React.FormEvent<HTMLButtonElement>) => {
		event.preventDefault();

		const model = GetUpdateModel();

		if (model) {
			model.markAsDone = event.currentTarget.name === "submitButton";
			updatePeerReview(model)
				.unwrap()
				.then(() => {
					navigate(`/home`);
				})
				.catch((error) => {
					console.log(error);
				});
		}
	};

	const handleProctorEdit = (event: React.FormEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setIsReadOnly(false);
	};

	const handleProctorCancel = (event: React.FormEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setIsReadOnly(true);
		setQuestions(props.review.questions);
	};

	const handleProctorSave = (event: React.FormEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setIsReadOnly(true);

		const model = GetUpdateModel();

		if (model) {
			model.markAsDone = true;
			updatePeerReview(model)
				.unwrap()
				.catch((error) => {
					console.log(error);
				});
		}
	};

	function GetUpdateModel(): PeerReviewUpdateModel | null {
		const model: PeerReviewUpdateModel = {
			draftId: props.draftId,
			reviewId: props.review.id,
			markAsDone: false,
			questionViewModels: questions.map((question) => ({
				id: question.id,
				answer: question.answer,
				comment: question.comment,
			})),
		};
		return model;
	}

	return (
		<div className={props.className ?? ""}>
			<PeerReviewTable
				readOnly={isReadOnly}
				review={props.review}
				questions={questions}
				questionIndex={questionIndex}
				inputRef={inputRef ? (inputRef as React.RefObject<HTMLTextAreaElement>) : undefined}
				onAnswerChange={!isReadOnly ? handleAnswerChange : undefined}
				onCommentChange={!isReadOnly ? handleCommentChange : undefined}
				onStarterChange={!isReadOnly ? handleStarterChange : undefined}
			/>
			{(props.showPrint || canUpdateReview) && (
				<div className="flex justify-between mt-4 print:hidden">
					<div>
						{props.showPrint && (
							<button
								id="print-peer-review-button"
								name="printButton"
								className="btn btn-neutral block mt-2 mr-4"
								onClick={handlePrint}>
								Print
							</button>
						)}
					</div>
					<div>
						{canUpdateReview && role === UserRole.Writer && (
							<>
								<button
									id="save-peer-review-button"
									name="saveButton"
									className="btn btn-secondary block mt-2 mr-4"
									onClick={handleSaveOrSubmit}>
									Save and Quit
								</button>
								<button
									id="submit-peer-review-button"
									name="submitButton"
									className="btn btn-primary block mt-2"
									onClick={handleSaveOrSubmit}
									disabled={!canSubmit()}>
									Submit
								</button>
							</>
						)}
						{canUpdateReview &&
							role === UserRole.Proctor &&
							(isReadOnly ? (
								<button
									id="edit-peer-review-button"
									name="EditButton"
									className="btn btn-primary block mt-2"
									onClick={handleProctorEdit}>
									Edit
								</button>
							) : (
								<>
									<button
										id="cancel-peer-review-button"
										name="CancelButton"
										className="btn btn-neutral block mt-2 mr-4"
										onClick={handleProctorCancel}>
										Cancel
									</button>
									<button
										id="save-peer-review-button"
										name="SaveButton"
										className="btn btn-primary block mt-2"
										onClick={handleProctorSave}>
										Save
									</button>
								</>
							))}
					</div>
				</div>
			)}
		</div>
	);
}

export interface PeerReviewTableProps {
	readOnly: boolean;
	review: PeerReviewViewModel;
	questions?: PeerReviewQuestionViewModel[];
	questionIndex?: number | undefined;
	inputRef?: React.RefObject<HTMLTextAreaElement>;
	onAnswerChange?: (index: number, value: string) => void;
	onStarterChange?: (index: number, value: string) => void;
	onCommentChange?: (index: number, value: string) => void;
}

export function PeerReviewTable(props: PeerReviewTableProps) {
	const generalQuestions = props.questions?.filter((q) => q.isGeneral == true);
	const otherQuestions = props.questions?.filter((q) => q.isGeneral == false);
	return (
		<table className="tbl tbl-data">
			<thead>
				<ReviewHeaderRow isGeneral={true} />
			</thead>
			<tbody>
				{generalQuestions?.map((row, index) => (
					<ReviewRow
						key={index}
						review={props.review}
						row={row}
						index={index}
						questionIndex={props.questionIndex}
						inputRef={props.inputRef}
						readOnly={props.readOnly}
						onAnswerChange={props.onAnswerChange}
						onCommentChange={props.onCommentChange}
						onStarterChange={props.onStarterChange}
					/>
				))}
			</tbody>
			{otherQuestions && otherQuestions.length > 0 && (
				<>
					<thead>
						<ReviewHeaderRow isGeneral={false} />
					</thead>
					<tbody>
						{otherQuestions?.map((row, index) => (
							//need to add 2 to index to account for the 2 indexes from the general 2 questions so the radio buttons line up correctly
							<ReviewRow
								key={index + 2}
								review={props.review}
								row={row}
								index={index + 2}
								questionIndex={props.questionIndex}
								inputRef={props.inputRef}
								readOnly={props.readOnly}
								onAnswerChange={props.onAnswerChange}
								onCommentChange={props.onCommentChange}
								onStarterChange={props.onStarterChange}
							/>
						))}
					</tbody>
				</>
			)}
		</table>
	);
}
interface ReviewHeaderRowProps {
	isGeneral: boolean;
	className?: string;
}
function ReviewHeaderRow({ isGeneral, className }: ReviewHeaderRowProps) {
	const headerId = "nested-header";
	const cols = isGeneral ? 4 : 1;
	return (
		<tr>
			<th
				id={isGeneral ? "" : headerId}
				className={`${className} question w-2/5 print:w-1/4`}>
				Review Question
			</th>
			{!isGeneral && (
				<>
					<th
						id={headerId}
						className={`${className} ysn !pr-1 !text-center print:w-[5%]`}>
						Yes
					</th>
					<th
						id={headerId}
						className={`${className} ysn !pr-1 !text-center print:w-[5%]`}>
						Somewhat
					</th>
					<th
						id={headerId}
						className={`${className} ysn !pr-1 !text-center print:w-[5%]`}>
						No
					</th>
				</>
			)}
			<th
				id={isGeneral ? "" : headerId}
				className={`${className} comment print:min-w-1/2`}
				colSpan={cols}>
				Comment
			</th>
		</tr>
	);
}
interface ReviewRowProps {
	readOnly: boolean;
	review: PeerReviewViewModel;
	row: PeerReviewQuestionViewModel;
	index: number;
	questionIndex?: number | undefined;
	inputRef?: React.RefObject<HTMLTextAreaElement>;
	onAnswerChange?: (index: number, value: string) => void;
	onStarterChange?: (index: number, value: string) => void;
	onCommentChange?: (index: number, value: string) => void;
}
function ReviewRow(props: ReviewRowProps) {
	const cols = props.row.isGeneral ? 4 : 1;
	return (
		<tr key={props.row.id}>
			<td className="question print:align-top">{props.row.question}</td>
			{!props.row.isGeneral && (
				<>
					<RadioCell
						row={props.row}
						index={props.index}
						value="Yes"
						readOnly={props.readOnly}
						onChange={props.onAnswerChange}
					/>
					<RadioCell
						row={props.row}
						index={props.index}
						value="Somewhat"
						readOnly={props.readOnly}
						onChange={props.onAnswerChange}
					/>
					<RadioCell
						row={props.row}
						index={props.index}
						value="No"
						readOnly={props.readOnly}
						onChange={props.onAnswerChange}
					/>
				</>
			)}
			<td colSpan={cols} className="comment">
				{!props.readOnly && props.onStarterChange && (
					<SentenceStarters
						showStarters={props.review.canShowSentenceStarters}
						row={props.row}
						index={props.index}
						onChange={props.onStarterChange}
					/>
				)}
				<AutosizeTextArea
					className="autosizable print:hidden"
					value={props.row.comment ?? ""}
					rows={2}
					data-id={props.row.id}
					readOnly={props.readOnly}
					spellCheck={!props.readOnly}
					onChange={(e) =>
						props.onCommentChange
							? props.onCommentChange(props.index, e.target.value)
							: undefined
					}
					ref={props.index === props.questionIndex ? props.inputRef : undefined}
				/>
				<div className="print:block hidden text-base p-2 border whitespace-pre-line">
					{props.row.comment}
				</div>
			</td>
		</tr>
	);
}
interface RadioCellProps {
	row: PeerReviewQuestionViewModel;
	index: number;
	value: string;
	readOnly: boolean;
	onChange?: (index: number, value: string) => void; //handleAnswerChange
}
function RadioCell(props: RadioCellProps) {
	return (
		<td className="ysn print:align-top !text-center">
			<input
				type="radio"
				name={`answer-${props.row.id}`}
				value={props.value}
				checked={props.row.answer === props.value}
				readOnly={props.readOnly}
				onChange={(e) =>
					props.onChange ? props.onChange(props.index, e.target.value) : undefined
				}
			/>
		</td>
	);
}
interface SentenceStartersProps {
	showStarters: boolean;
	row: PeerReviewQuestionViewModel;
	index: number;
	onChange: (index: number, value: string) => void; //handleStarterChange
}
function SentenceStarters(props: SentenceStartersProps) {
	if (!(props.showStarters && props.row.sentenceStarters.length > 0)) return null;
	return (
		<SimpleListbox
			id={`starter-${props.row.id}`}
			options={[
				{
					value: "",
					label: "Sentence Starters",
				},
			].concat(
				props.row.sentenceStarters.map((ss, index) => {
					return {
						value: ss.replace("…", " "),
						label: ss,
					};
				})
			)}
			value=""
			className={"w-full print:hidden"}
			onChange={(e) => props.onChange(props.index, e.target.value)}
		/>
	);
}
